import { PrismicRichText } from '@prismicio/react'
import { Link, graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React, { useEffect, useRef, useState } from 'react'
import * as GlobalStyles from 'src/styles/global.module.scss'
import {
  FooterQuery,
  PrismicGlobalContentDataType,
  PrismicLinkType,
  PrismicNavigationDataNavigationGroups,
  PrismicNavigationGroup,
  PrismicSiteInformationDataSocialMediaPlatforms,
  PrismicSiteInformationDataType,
  PrismicStructuredTextType,
} from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import { useTranslation } from 'src/utils/useTranslation'
import { LocaleKey, siteMetadata } from '../../../static/siteMetadata'
import PrivacyIcon from '../../images/svgs/icons/privacyoptions.svg'
import BertholdTitle from '../atoms/bertholdTitle'
import * as Styles from './footer.module.scss'
import FooterNavigationGroup from './footerNavigationGroup'

const Footer = () => {
  const cookiePreferencesLinkRef = useRef<HTMLAnchorElement>(null)
  const { t } = useTranslation()

  // Handle focus management for cookie preferences modal
  const handleCookiePreferencesClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault()

    // Store reference to the clicked element for focus return
    const triggerElement = cookiePreferencesLinkRef.current

    // Open the OneTrust modal directly
    if (typeof window !== 'undefined' && (window as any).OneTrust) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(window as any).OneTrust.ToggleInfoDisplay()
    }

    // Find and observe the close button(s) in the OneTrust modal
    const setupCloseHandler = () => {
      const closeButton = document.querySelector(
        '#close-pc-btn-handler, .ot-close-icon, .onetrust-close-btn-handler, [aria-label="Close"]'
      )
      const saveButton = document.querySelector(
        '.save-preference-btn-handler, .ot-pc-refuse-all-handler, .accept-all-handler'
      )

      const handleClose = () => {
        // Use setTimeout to allow the modal to fully close before focusing
        setTimeout(() => {
          if (triggerElement) {
            triggerElement.focus()
          }
        }, 100)
      }

      if (closeButton) {
        closeButton.addEventListener('click', handleClose, { once: true })
      }
      if (saveButton) {
        saveButton.addEventListener('click', handleClose, { once: true })
      }

      // Also observe for modal being hidden via class changes
      const modal = document.getElementById('onetrust-pc-sdk')
      if (modal) {
        const observer = new MutationObserver(() => {
          if (
            !modal.classList.contains('ot-slide-in-show') &&
            modal.getAttribute('aria-hidden') === 'true'
          ) {
            handleClose()
            observer.disconnect()
          }
        })
        observer.observe(modal, {
          attributes: true,
          attributeFilter: ['class', 'aria-hidden'],
        })

        // Fallback: disconnect after 2 minutes
        setTimeout(() => observer.disconnect(), 120000)
      }
    }

    // Wait for the modal to appear in the DOM
    setTimeout(setupCloseHandler, 300)
  }

  const data: FooterQuery = useStaticQuery(graphql`
    query Footer {
      site {
        siteMetadata {
          locale
        }
      }
      prismicGlobalContent {
        data {
          footer_cta_1_title
          footer_cta_1_description {
            richText
          }
          footer_cta_1_link {
            ...BasicLinkFragment
          }
          footer_cta_1_link_text
          footer_cta_1_image {
            gatsbyImageData
          }
          footer_cta_2_title
          footer_cta_2_description {
            richText
          }
          footer_cta_2_link {
            ...BasicLinkFragment
          }
          footer_cta_2_link_text
          footer_cta_2_image {
            gatsbyImageData
          }
          footer_legal {
            legal_button_text
            legal_button_link {
              ...BasicLinkFragment
            }
          }
          footer_cookie_preferences_button_text
        }
      }
      prismicSiteInformation {
        data {
          site_icon {
            gatsbyImageData(layout: CONSTRAINED, aspectRatio: 1.5)
            alt
          }
          social_media_platforms {
            link {
              ...BasicLinkFragment
            }
            name
            white_icon {
              gatsbyImageData(layout: CONSTRAINED, height: 20)
              alt
            }
          }
          home_page {
            url
          }
        }
      }
      prismicNavigation {
        data {
          navigation_groups {
            navigation_group {
              document {
                ...NavigationGroupFragment
              }
            }
          }
        }
      }
    }
  `)

  // Get current locale from site metadata
  const currentLocale =
    ((data as any).site?.siteMetadata?.locale as LocaleKey) || LocaleKey.EN_US

  const [isLocaleDropdownOpen, setIsLocaleDropdownOpen] = useState(false)
  const [selectedLocale, setSelectedLocale] = useState<LocaleKey>(currentLocale)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  const locales = Object.entries(siteMetadata).map(([key, value]) => ({
    key: key as LocaleKey,
    code: value.code,
    label: value.label,
    url: value.url,
  }))

  // Find current selected index for keyboard navigation
  const selectedIndex = locales.findIndex(
    locale => locale.key === selectedLocale
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocaleDropdownOpen(false)
        setFocusedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Focus management when dropdown opens
  useEffect(() => {
    if (
      isLocaleDropdownOpen &&
      focusedIndex >= 0 &&
      optionRefs.current[focusedIndex]
    ) {
      optionRefs.current[focusedIndex]?.focus()
    }
  }, [isLocaleDropdownOpen, focusedIndex])

  // Keyboard handler for the combobox trigger
  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault()
        if (!isLocaleDropdownOpen) {
          setIsLocaleDropdownOpen(true)
          setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
        } else {
          setFocusedIndex(0)
        }
        break
      case 'ArrowUp':
      case 'Up':
        event.preventDefault()
        if (!isLocaleDropdownOpen) {
          setIsLocaleDropdownOpen(true)
          setFocusedIndex(
            selectedIndex >= 0 ? selectedIndex : locales.length - 1
          )
        } else {
          setFocusedIndex(locales.length - 1)
        }
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (!isLocaleDropdownOpen) {
          setIsLocaleDropdownOpen(true)
          setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
        }
        break
      case 'Escape':
        event.preventDefault()
        setIsLocaleDropdownOpen(false)
        setFocusedIndex(-1)
        break
      default:
        break
    }
  }

  // Keyboard handler for options in the listbox
  const handleOptionKeyDown = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault()
        if (index < locales.length - 1) {
          setFocusedIndex(index + 1)
        } else {
          setFocusedIndex(0) // Wrap to first
        }
        break
      case 'ArrowUp':
      case 'Up':
        event.preventDefault()
        if (index > 0) {
          setFocusedIndex(index - 1)
        } else {
          setFocusedIndex(locales.length - 1) // Wrap to last
        }
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        setSelectedLocale(locales[index].key)
        setIsLocaleDropdownOpen(false)
        setFocusedIndex(-1)
        window.location.href = locales[index].url
        break
      case 'Escape': {
        event.preventDefault()
        setIsLocaleDropdownOpen(false)
        setFocusedIndex(-1)
        // Return focus to trigger button
        const triggerButton = dropdownRef.current?.querySelector('button')
        triggerButton?.focus()
        break
      }
      case 'Tab':
        // Close dropdown on tab out
        setIsLocaleDropdownOpen(false)
        setFocusedIndex(-1)
        break
      case 'Home':
        event.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        event.preventDefault()
        setFocusedIndex(locales.length - 1)
        break
      default:
        break
    }
  }

  const footerContent = data.prismicGlobalContent
    ?.data as PrismicGlobalContentDataType
  const siteInformation = data.prismicSiteInformation
    ?.data as PrismicSiteInformationDataType

  const icon = siteInformation?.site_icon?.gatsbyImageData as IGatsbyImageData
  const iconAlt = siteInformation.site_icon?.alt ?? ''
  const socialMedia =
    siteInformation.social_media_platforms as PrismicSiteInformationDataSocialMediaPlatforms[]
  const homepage = siteInformation?.home_page?.url ?? '/'

  const navigationGroups =
    (data.prismicNavigation?.data
      .navigation_groups as PrismicNavigationDataNavigationGroups[]) ?? []

  const displayCtas =
    footerContent.footer_cta_1_title &&
    footerContent.footer_cta_1_link &&
    footerContent.footer_cta_2_title &&
    footerContent.footer_cta_2_link

  // Render

  const listboxId = 'locale-listbox'

  const renderLocaleSwitcher = () => (
    <div className={Styles.localeWrapper} ref={dropdownRef}>
      <button
        type="button"
        className={Styles.localeButton}
        onClick={() => {
          setIsLocaleDropdownOpen(!isLocaleDropdownOpen)
          if (!isLocaleDropdownOpen) {
            setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
          } else {
            setFocusedIndex(-1)
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        role="combobox"
        aria-expanded={isLocaleDropdownOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={
          isLocaleDropdownOpen && focusedIndex >= 0
            ? `locale-option-${focusedIndex}`
            : undefined
        }
      >
        <svg
          className={Styles.globeIcon}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM13.9 5H11.6C11.4 3.9 11 2.9 10.5 2C12.1 2.6 13.4 3.7 13.9 5ZM8 2C8.7 2.9 9.3 4 9.5 5H6.5C6.7 4 7.3 2.9 8 2ZM2.3 10C2.1 9.4 2 8.7 2 8C2 7.3 2.1 6.6 2.3 6H5C4.9 6.6 4.9 7.3 4.9 8C4.9 8.7 5 9.4 5 10H2.3ZM2.1 11H4.4C4.6 12.1 5 13.1 5.5 14C3.9 13.4 2.6 12.3 2.1 11ZM4.4 5H2.1C2.6 3.7 3.9 2.6 5.5 2C5 2.9 4.6 3.9 4.4 5ZM8 14C7.3 13.1 6.7 12 6.5 11H9.5C9.3 12 8.7 13.1 8 14ZM9.8 10H6.2C6.1 9.4 6 8.7 6 8C6 7.3 6.1 6.6 6.2 6H9.8C9.9 6.6 10 7.3 10 8C10 8.7 9.9 9.4 9.8 10ZM10.5 14C11 13.1 11.4 12.1 11.6 11H13.9C13.4 12.3 12.1 13.4 10.5 14ZM11.1 10C11.2 9.4 11.2 8.7 11.2 8C11.2 7.3 11.1 6.6 11.1 6H13.8C14 6.6 14.1 7.3 14.1 8C14.1 8.7 14 9.4 13.8 10H11.1Z"
            fill="currentColor"
          />
        </svg>
        <span className={Styles.localeText}>
          {siteMetadata[selectedLocale].code}
        </span>
        <svg
          className={`${Styles.chevronIcon} ${
            isLocaleDropdownOpen ? Styles.chevronOpen : ''
          }`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
            fill="currentColor"
          />
        </svg>
      </button>
      {isLocaleDropdownOpen && (
        <ul
          id={listboxId}
          ref={listboxRef}
          className={Styles.localeDropdown}
          role="listbox"
          aria-label={t('pageLayout.footer.localeSwitcher.localeListAriaLabel')}
        >
          {locales.map((locale, index) => (
            <li key={locale.key} role="presentation">
              <button
                type="button"
                id={`locale-option-${index}`}
                ref={el => {
                  optionRefs.current[index] = el
                }}
                className={`${Styles.localeOption} ${
                  selectedLocale === locale.key ? Styles.localeActive : ''
                }`}
                role="option"
                aria-selected={selectedLocale === locale.key}
                tabIndex={focusedIndex === index ? 0 : -1}
                onClick={() => {
                  setSelectedLocale(locale.key)
                  setIsLocaleDropdownOpen(false)
                  setFocusedIndex(-1)
                  window.location.href = locale.url
                }}
                onKeyDown={e => handleOptionKeyDown(e, index)}
              >
                {locale.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  const renderCTA = (
    title: string,
    description: PrismicStructuredTextType | undefined,
    link: PrismicLinkType | undefined,
    linkText: string,
    image: IGatsbyImageData,
    imageAlt: string
  ) => {
    if (!displayCtas) return null

    return (
      <div className={Styles.footerCta}>
        <div className={Styles.content}>
          <h2 className={Styles.title}>{BertholdTitle({ text: title })}</h2>
          <PrismicRichText
            field={description?.richText}
            components={{
              hyperlink: ({ node, children }: any) =>
                CustomLink({ link: node.data, children }),
            }}
          />
          <div className={Styles.linkWrapper}>
            <CustomLink
              link={link}
              className={`${GlobalStyles.whiteGhostButton} ${Styles.link}`}
            >
              {linkText ?? ''}
            </CustomLink>
          </div>
        </div>
        {!!image && (
          <div className={Styles.image}>
            <GatsbyImage image={image} alt={imageAlt} />
          </div>
        )}
      </div>
    )
  }

  return (
    <footer data-testid="footer" className={Styles.footer}>
      <div className={`${Styles.upperContainer}`}>
        <div className={Styles.mainContainer}>
          <div className={`${Styles.logoContainer} ${Styles.selectDisable}`}>
            <Link to={homepage}>
              {!!icon && (
                <GatsbyImage
                  className={Styles.logo}
                  data-testid="canes-logo"
                  image={icon}
                  alt={iconAlt}
                />
              )}
            </Link>
          </div>
          <ul
            data-testid="social-media-container"
            className={`${Styles.socialMediaContainer} ${Styles.selectDisable}`}
          >
            {socialMedia.map((socialMediaItem: any, index: number) => {
              const key = `socialMedia-${index}`
              return (
                <li className={Styles.socialMediaLink} key={key}>
                  <CustomLink link={socialMediaItem.link}>
                    <GatsbyImage
                      data-testid="social-media-image"
                      image={socialMediaItem.white_icon.gatsbyImageData}
                      alt={socialMediaItem.white_icon.alt ?? ''}
                    />
                  </CustomLink>
                </li>
              )
            })}
          </ul>
        </div>
        <div className={Styles.divider} />
        <div className={Styles.navContainer}>
          {navigationGroups.map(
            (
              navigationGroup: PrismicNavigationDataNavigationGroups,
              i: number
            ) => {
              const key = `navigation-group-${i}`
              const group = navigationGroup.navigation_group
                ?.document as PrismicNavigationGroup

              return <FooterNavigationGroup key={key} group={group} />
            }
          )}
        </div>
      </div>
      <div className={`${Styles.lowerContainer}`}>
        {renderCTA(
          footerContent.footer_cta_1_title ?? '',
          footerContent.footer_cta_1_description ?? undefined,
          footerContent.footer_cta_1_link ?? undefined,
          footerContent.footer_cta_1_link_text ?? '',
          footerContent.footer_cta_1_image?.gatsbyImageData,
          footerContent.footer_cta_1_image?.alt ?? ''
        )}
        {renderCTA(
          footerContent.footer_cta_2_title ?? '',
          footerContent.footer_cta_2_description ?? undefined,
          footerContent.footer_cta_2_link ?? undefined,
          footerContent.footer_cta_2_link_text ?? '',
          footerContent.footer_cta_2_image?.gatsbyImageData ?? '',
          footerContent.footer_cta_2_image?.alt ?? ''
        )}
      </div>

      <div data-testid="copyright-item" className={Styles.copyrightContainer}>
        {footerContent.footer_legal &&
          footerContent.footer_legal.map((link, i) => {
            if (!link) return null
            const key = `legal-link-${i}`
            return (
              <div className={Styles.copyrightLinkWrapper} key={key}>
                <CustomLink
                  className={Styles.copyrightLinkText}
                  link={link.legal_button_link}
                >
                  {link.legal_button_text ?? ''}
                </CustomLink>
              </div>
            )
          })}

        <div className={Styles.copyrightLinkWrapper}>
          <a
            ref={cookiePreferencesLinkRef}
            key="temp-cookies-link"
            className={Styles.copyrightLinkText}
            href="#show-cookie-preferences"
            onClick={handleCookiePreferencesClick}
          >
            <div className={Styles.privacyChoices}>
              <span>
                {footerContent.footer_cookie_preferences_button_text ?? ''}
              </span>
              <PrivacyIcon />
            </div>
          </a>
        </div>

        {renderLocaleSwitcher()}
      </div>
    </footer>
  )
}

export default Footer
